import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Search, Download, Mail, Globe2, Briefcase, Building2, TrendingUp,
  Filter, X, Copy, Check, ChevronRight, ChevronDown, ExternalLink, Phone,
} from "lucide-react";
import { adminFetchDistributorApplications, isAdminAuthed } from "@/lib/blogApi";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};
const fmtDay = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
};

const csvEscape = (v) => {
  const s = v == null ? "" : String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};
const FIELDS = [
  "created_at","company_name","contact_person","email","phone","website","country","region_served",
  "business_type","application_category","grades_of_interest","expected_annual_volume",
  "target_launch_timeline","regulatory_needs","exclusivity_interest","message",
];

const AdminDistributorApplications = () => {
  const authed = isAdminAuthed();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("All");
  const [businessType, setBusinessType] = useState("All");
  const [expanded, setExpanded] = useState(new Set());
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (!authed) return;
    let alive = true;
    setLoading(true);
    adminFetchDistributorApplications()
      .then((r) => { if (alive) setRows(r || []); })
      .catch(() => toast.error("Could not load distributor applications"))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [authed]);

  const countries = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => { if (r.country) set.add(r.country); });
    return ["All", ...Array.from(set).sort()];
  }, [rows]);
  const businessTypes = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => { if (r.business_type) set.add(r.business_type); });
    return ["All", ...Array.from(set).sort()];
  }, [rows]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (country !== "All" && r.country !== country) return false;
      if (businessType !== "All" && r.business_type !== businessType) return false;
      if (!term) return true;
      return (
        (r.company_name || "").toLowerCase().includes(term) ||
        (r.contact_person || "").toLowerCase().includes(term) ||
        (r.email || "").toLowerCase().includes(term) ||
        (r.region_served || "").toLowerCase().includes(term) ||
        (r.application_category || "").toLowerCase().includes(term) ||
        (r.message || "").toLowerCase().includes(term) ||
        (r.grades_of_interest || []).join(",").toLowerCase().includes(term)
      );
    });
  }, [rows, search, country, businessType]);

  const stats = useMemo(() => {
    const total = rows.length;
    const now = Date.now();
    const last30 = rows.filter((r) => { try { return now - new Date(r.created_at).getTime() < 30 * 86400000; } catch { return false; } }).length;
    const uniqueCountries = new Set(rows.map((r) => r.country).filter(Boolean)).size;
    const typeCounts = {};
    rows.forEach((r) => { if (r.business_type) typeCounts[r.business_type] = (typeCounts[r.business_type] || 0) + 1; });
    let top = ["—", 0];
    Object.entries(typeCounts).forEach(([k, v]) => { if (v > top[1]) top = [k, v]; });
    return { total, last30, uniqueCountries, topType: top[0], topTypeCount: top[1] };
  }, [rows]);

  const toggleExpand = (id) => setExpanded((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const exportCsv = () => {
    const lines = [FIELDS.join(",")];
    filtered.forEach((r) => {
      lines.push(FIELDS.map((h) => csvEscape(Array.isArray(r[h]) ? r[h].join("; ") : r[h])).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `spiruva-distributor-applications-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 500);
    toast.success(`Exported ${filtered.length} application${filtered.length === 1 ? "" : "s"}`);
  };
  const copyEmail = async (id, email) => {
    try {
      await navigator.clipboard.writeText(email);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1600);
    } catch { toast.error("Could not copy"); }
  };
  const mailtoHref = (r) => {
    const subject = `Re: Distributor application — ${r.company_name}`;
    const body = `Hi ${r.contact_person || "there"},\n\nThank you for applying to SPIRUVA's distributor portal for the July 2027 rollout. We've reviewed your interest in ${(r.grades_of_interest || []).join(", ") || "our portfolio"} for ${r.application_category || "your market"}.\n\nLet me know a 20-minute window this week to discuss territory fit and onboarding.\n\nBest,\nSPIRUVA Partner Desk\nexport@spiruva.com  ·  spiruva.com`;
    return `mailto:${encodeURIComponent(r.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };
  const clearFilters = () => { setSearch(""); setCountry("All"); setBusinessType("All"); };
  const hasFilter = search || country !== "All" || businessType !== "All";

  if (!authed) return <Navigate to="/admin" replace />;

  return (
    <div className="pt-[72px] bg-brand-cream/40 min-h-screen" data-testid="page-admin-distributor-applications">
      <div className="container-wide py-10">
        <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-sm text-brand-black/65 hover:text-brand-teal mb-6">
          <ArrowLeft size={14} /> Back to Dashboard
        </Link>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
          <div>
            <p className="eyebrow mb-2">◦  Distributor Applications</p>
            <h1 className="font-display text-4xl text-brand-black">Launch-Partner Intake</h1>
            <p className="text-sm text-brand-black/60 mt-2 max-w-xl">
              Applications submitted via the public <code className="font-mono text-brand-teal">/distributor-portal</code> for SPIRUVA's July 2027 partner rollout. A copy of every application is also emailed in real time.
            </p>
          </div>
          <button
            onClick={exportCsv}
            disabled={filtered.length === 0}
            data-testid="distributor-export-csv"
            className="btn-primary disabled:opacity-50 min-h-[44px]"
          >
            <Download size={14} /> Export CSV ({filtered.length})
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-brand-green/15 border border-brand-green/15 mb-8">
          {[
            { icon: Building2, l: "Total Applications", v: stats.total },
            { icon: TrendingUp, l: "Last 30 Days", v: stats.last30 },
            { icon: Globe2, l: "Unique Countries", v: stats.uniqueCountries },
            { icon: Briefcase, l: "Top Partner Type", v: stats.topType, sub: stats.topTypeCount > 0 ? `${stats.topTypeCount} applications` : "" },
          ].map((s) => (
            <div key={s.l} className="bg-white p-6">
              <s.icon size={14} className="text-brand-teal" />
              <p className="font-display text-3xl text-brand-black mt-3 line-clamp-1">{s.v}</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/50 mt-1">{s.l}</p>
              {s.sub && <p className="text-[11px] text-brand-black/55 mt-2">{s.sub}</p>}
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white border border-brand-green/15 p-4 mb-4 flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Search size={14} className="text-brand-black/40 shrink-0" />
            <input
              type="text"
              placeholder="Search by company, contact, region, application, grade…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="distributor-search"
              className="flex-1 bg-transparent text-sm focus:outline-none min-w-0 min-h-[36px]"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={13} className="text-brand-black/40" />
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              data-testid="distributor-filter-country"
              className="px-3 py-2 bg-brand-cream/40 border border-brand-green/20 text-xs font-mono uppercase tracking-widest focus:outline-none min-h-[36px]"
            >
              {countries.map((c) => <option key={c}>{c}</option>)}
            </select>
            <select
              value={businessType}
              onChange={(e) => setBusinessType(e.target.value)}
              data-testid="distributor-filter-business-type"
              className="px-3 py-2 bg-brand-cream/40 border border-brand-green/20 text-xs font-mono uppercase tracking-widest focus:outline-none min-h-[36px]"
            >
              {businessTypes.map((c) => <option key={c}>{c}</option>)}
            </select>
            {hasFilter && (
              <button onClick={clearFilters} data-testid="distributor-clear-filters" className="text-xs font-mono uppercase tracking-widest text-brand-black/60 hover:text-brand-gold inline-flex items-center gap-1">
                <X size={12} /> Clear
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-brand-green/15 overflow-x-auto">
          <table className="clinical-table">
            <thead>
              <tr>
                <th className="w-6"></th>
                <th>Date</th>
                <th>Company</th>
                <th>Contact</th>
                <th>Country / Region</th>
                <th>Business Type</th>
                <th>Application</th>
                <th>Grades</th>
                <th>Volume / Timeline</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="text-center text-brand-black/50" data-testid="distributor-loading">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={10} className="text-center text-brand-black/50 py-10" data-testid="distributor-empty">
                  {rows.length === 0
                    ? "No distributor applications yet. Submissions from /distributor-portal will appear here in real time."
                    : "No applications match your filters."}
                </td></tr>
              ) : filtered.flatMap((r) => {
                const open = expanded.has(r.id);
                const tr = (
                  <tr key={r.id} data-testid={`distributor-row-${r.id}`}>
                    <td>
                      <button
                        onClick={() => toggleExpand(r.id)}
                        aria-label={open ? "Collapse" : "Expand"}
                        data-testid={`distributor-expand-${r.id}`}
                        className="text-brand-black/40 hover:text-brand-teal transition"
                      >
                        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </button>
                    </td>
                    <td>
                      <p className="text-xs text-brand-black/80">{fmtDay(r.created_at)}</p>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/40 mt-0.5">{fmtDate(r.created_at).split(", ").pop()}</p>
                    </td>
                    <td className="max-w-[180px]">
                      <p className="font-medium text-brand-black text-sm line-clamp-2">{r.company_name}</p>
                      {r.website && (
                        <a href={r.website} target="_blank" rel="noopener noreferrer" className="text-[11px] text-brand-teal hover:text-brand-gold font-mono inline-flex items-center gap-1 mt-0.5">
                          {r.website.replace(/^https?:\/\//, "").slice(0, 24)}{r.website.length > 24 ? "…" : ""} <ExternalLink size={9} />
                        </a>
                      )}
                    </td>
                    <td>
                      <p className="font-medium text-brand-black text-sm">{r.contact_person || "—"}</p>
                      <button
                        onClick={() => copyEmail(r.id, r.email)}
                        className="inline-flex items-center gap-1.5 text-xs text-brand-teal hover:text-brand-green font-mono mt-0.5"
                        title="Copy email"
                      >
                        {r.email}
                        {copiedId === r.id ? <Check size={11} className="text-brand-green" /> : <Copy size={10} className="opacity-50" />}
                      </button>
                    </td>
                    <td>
                      <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-brand-green/25 text-brand-green bg-brand-green/5 inline-block">{r.country}</span>
                      {r.region_served && <p className="text-[11px] text-brand-black/55 mt-1">{r.region_served}</p>}
                    </td>
                    <td>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-brand-black/75">{r.business_type || "—"}</span>
                    </td>
                    <td className="max-w-[150px]">
                      <span className="text-xs text-brand-black/80">{r.application_category || "—"}</span>
                    </td>
                    <td className="max-w-[160px]">
                      <div className="flex flex-wrap gap-1">
                        {(r.grades_of_interest || []).slice(0, 4).map((g) => (
                          <span key={g} className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 bg-brand-teal/10 text-brand-teal border border-brand-teal/25">{g}</span>
                        ))}
                        {(r.grades_of_interest || []).length > 4 && (
                          <span className="text-[10px] font-mono text-brand-black/50">+{r.grades_of_interest.length - 4}</span>
                        )}
                      </div>
                    </td>
                    <td className="max-w-[140px]">
                      <p className="text-xs text-brand-black/80">{r.expected_annual_volume || "—"}</p>
                      <p className="text-[11px] text-brand-black/55 mt-0.5">{r.target_launch_timeline || ""}</p>
                    </td>
                    <td>
                      <a
                        href={mailtoHref(r)}
                        data-testid={`distributor-email-${r.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-green text-brand-cream text-[10px] font-mono uppercase tracking-widest hover:bg-brand-black transition"
                      >
                        <Mail size={11} /> Reply
                      </a>
                    </td>
                  </tr>
                );
                if (!open) return [tr];
                const detail = (
                  <tr key={`${r.id}-detail`} data-testid={`distributor-detail-${r.id}`} className="bg-brand-cream/40">
                    <td colSpan={10} className="!py-6 !px-8">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <DetailField label="Phone / WhatsApp" value={r.phone} icon={Phone} />
                        <DetailField label="Region Served" value={r.region_served} />
                        <DetailField label="Exclusivity Interest" value={r.exclusivity_interest} />
                        <DetailField label="Expected Annual Volume" value={r.expected_annual_volume} />
                        <DetailField label="Target Launch Timeline" value={r.target_launch_timeline} />
                        <DetailField label="Website" value={r.website} />
                        <DetailField label="Regulatory / Documentation Needs" value={r.regulatory_needs} className="md:col-span-3" multiline />
                        <DetailField label="Partnership Notes" value={r.message} className="md:col-span-3" multiline />
                      </div>
                    </td>
                  </tr>
                );
                return [tr, detail];
              })}
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-brand-black/45 mt-8 font-mono">
          Applications stored in MongoDB collection <span className="text-brand-teal">distributor_applications</span>. A real-time email copy is sent via Resend to the partner desk.
        </p>
      </div>
    </div>
  );
};

const DetailField = ({ label, value, multiline = false, className = "", icon: Icon }) => (
  <div className={className}>
    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-black/45 mb-1.5 inline-flex items-center gap-1.5">
      {Icon && <Icon size={11} />} {label}
    </p>
    <p className={`text-sm text-brand-black/80 ${multiline ? "whitespace-pre-wrap leading-relaxed" : ""}`}>
      {value || <span className="text-brand-black/30">—</span>}
    </p>
  </div>
);

export default AdminDistributorApplications;
